/**
 * t331u B4: phone Person › Availability (desktop MyShiftsPage AvailabilityEditor with a userId): the weekly pattern and
 * one-off dates, each with Add (a sheet) and a confirmed remove. What Add sends comes from shared personSettings.ts.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Plus, X } from 'lucide-react';
import {
  AVAILABILITY_KINDS, AVAILABILITY_NOTE, availabilityExceptionBody, availabilityRuleBody, availabilityTimes, availabilityTimesError, emptyAvailability, sortRules,
  WEEKDAY_NAMES, type AvailabilityDraft, type AvailabilityRule,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { qk } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Select, Spinner, ask } from '@/components/ui';
import { BottomSheet } from '../BottomSheet';
import { F, head, saveBtn } from './PersonSettings';

const kindLabel = (k: AvailabilityRule['kind']) => AVAILABILITY_KINDS.find(x => x.id === k)?.label ?? k;
const kindTone = (k: AvailabilityRule['kind']) => (k === 'unavailable' ? 'text-rose-700 dark:text-rose-400' : k === 'preferred' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-400');
const boxCls = 'mx-3 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900';
const addCls = 'flex min-h-[48px] w-full items-center gap-3 px-3 text-left text-base font-medium active:bg-slate-100 dark:active:bg-slate-800';

function Row({ title, line, kind, note, onRemove, testId }: { title: string; line: string; kind: AvailabilityRule['kind']; note: string | null; onRemove: () => void; testId: string }) {
  return (
    <div className="flex min-h-[56px] items-center border-b border-slate-100 pl-3 dark:border-slate-800" data-testid={testId}>
      <span className="min-w-0 flex-1 py-2">
        <span className="block truncate text-base">{title}</span>
        <span className="block truncate text-sm"><span className={kindTone(kind)}>{kindLabel(kind)}</span><span className="text-slate-600 dark:text-slate-400"> · {line}{note ? ` · ${note}` : ''}</span></span>
      </span>
      <button type="button" onClick={onRemove} aria-label={`Remove ${title}`} className="flex h-11 w-11 shrink-0 items-center justify-center text-slate-500"><X className="h-5 w-5" aria-hidden /></button>
    </div>
  );
}

export function AvailabilityPage({ userId, name }: { userId: string; name: string }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const av = useQuery({ queryKey: qk.wf(ws ?? '', 'availability', userId), queryFn: () => api.wf.availability(ws!, userId), enabled: Boolean(ws) });
  const [adding, setAdding] = useState<'rule' | 'date' | null>(null);
  const run = async (fn: () => Promise<unknown>, ok: string) => { try { await fn(); toast(ok, 'success'); void av.refetch(); return true; } catch (e) { toast(errorMessage(e), 'error'); return false; } };
  const remove = async (what: string, fn: () => Promise<unknown>) => { if (await ask({ title: `Remove ${what}?`, confirmLabel: 'Remove' })) await run(fn, 'Removed'); };
  const rules = sortRules(av.data?.rules ?? []);
  const dates = [...(av.data?.exceptions ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-availability">
      {name && <p className="truncate px-4 pt-3 text-base font-semibold" data-testid="m-emp-name">{name}</p>}
      {av.isLoading ? <div className="p-4"><Spinner /></div> : av.error ? <p className="p-4 text-sm text-rose-700">{errorMessage(av.error)}</p> : <>
        <h3 className={head}>Weekly pattern</h3>
        <div className={boxCls} data-testid="m-avail-rules">
          {rules.map(r => <Row key={r.id} testId={`m-avail-rule-${r.id}`} title={WEEKDAY_NAMES[r.weekday - 1]} kind={r.kind} line={availabilityTimes(r)} note={r.note}
            onRemove={() => void remove(`${WEEKDAY_NAMES[r.weekday - 1]} ${kindLabel(r.kind).toLowerCase()}`, () => api.wf.deleteAvailabilityRule(ws!, r.id))} />)}
          {!rules.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No weekly pattern, so available any time.</p>}
          <button type="button" className={addCls} onClick={() => setAdding('rule')} data-testid="m-avail-add-rule"><Plus className="h-5 w-5" aria-hidden />Add to weekly pattern</button>
        </div>
        <h3 className={head}>One-off dates</h3>
        <div className={boxCls} data-testid="m-avail-dates">
          {dates.map(x => { const d = DateTime.fromISO(x.date).toFormat('ccc d LLL yyyy'); return <Row key={x.id} testId={`m-avail-date-${x.id}`} title={d} kind={x.kind} line={availabilityTimes(x)} note={x.note}
            onRemove={() => void remove(d, () => api.wf.deleteAvailabilityException(ws!, x.id))} />; })}
          {!dates.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">None.</p>}
          <button type="button" className={addCls} onClick={() => setAdding('date')} data-testid="m-avail-add-date"><Plus className="h-5 w-5" aria-hidden />Add a date</button>
        </div>
        <p className="px-4 pt-3 text-xs text-slate-500">{AVAILABILITY_NOTE}</p>
      </>}
      {adding && <AddSheet mode={adding} onClose={() => setAdding(null)}
        onAdd={d => run(() => (adding === 'rule' ? api.wf.addAvailabilityRule(ws!, availabilityRuleBody(userId, d)) : api.wf.addAvailabilityException(ws!, availabilityExceptionBody(userId, d))), 'Added')} />}
    </div>
  );
}

function AddSheet({ mode, onClose, onAdd }: { mode: 'rule' | 'date'; onClose: () => void; onAdd: (d: AvailabilityDraft) => Promise<boolean> }) {
  const [d, setD] = useState(emptyAvailability);
  const [busy, setBusy] = useState(false);
  const err = availabilityTimesError(d);
  const blocked = Boolean(err) || (mode === 'date' && !d.date);
  return (
    <BottomSheet open onClose={onClose} title={mode === 'rule' ? 'Add to weekly pattern' : 'Add a date'} testId="m-avail-sheet"
      footer={<div className="px-3 py-2"><button type="button" className={clsx(saveBtn, 'w-full')} disabled={blocked || busy} data-testid="m-avail-save"
        onClick={async () => { setBusy(true); const ok = await onAdd(d); setBusy(false); if (ok) onClose(); }}>Add</button></div>}>
      <div className="px-4 pb-2">
        {mode === 'rule'
          ? <F label="Day"><Select width="full" value={d.weekday} data-testid="m-avail-day" onChange={e => setD({ ...d, weekday: Number(e.target.value) })}>{WEEKDAY_NAMES.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}</Select></F>
          : <F label="Date"><Input width="full" type="date" value={d.date} data-testid="m-avail-date" onChange={e => setD({ ...d, date: e.target.value })} /></F>}
        <div className="py-1.5" role="radiogroup" aria-label="Kind">
          <span className="mb-1 block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">Kind</span>
          <div className="flex gap-2">{AVAILABILITY_KINDS.map(k => (
            <button key={k.id} type="button" role="radio" aria-checked={d.kind === k.id} onClick={() => setD({ ...d, kind: k.id })} data-testid={`m-avail-kind-${k.id}`}
              className={clsx('h-11 flex-1 rounded-xl border text-sm font-medium', d.kind === k.id ? 'border-ink bg-ink text-white dark:border-white dark:bg-white dark:text-ink' : 'border-slate-300 dark:border-slate-700')}>{k.label}</button>
          ))}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="From"><Input width="full" type="time" value={d.start_time} data-testid="m-avail-from" onChange={e => setD({ ...d, start_time: e.target.value })} /></F>
          <F label="To"><Input width="full" type="time" value={d.end_time} data-testid="m-avail-to" onChange={e => setD({ ...d, end_time: e.target.value })} /></F>
        </div>
        <p className={clsx('text-xs', err ? 'text-rose-700' : 'text-slate-500')}>{err ?? 'Leave both times blank for all day.'}</p>
        <F label="Note"><Input width="full" placeholder="e.g. school run" value={d.note} onChange={e => setD({ ...d, note: e.target.value })} /></F>
      </div>
    </BottomSheet>
  );
}
